import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FaIcon, FloatingPanel, NgButton, PanelErrorWithIcon, PanelInfoWithIcon } from '@/ui';
import type { FloatingPanelState } from '@/ui';
import { LiveVideoSource } from '@/signalr';
import type { LiveVideo } from '@/signalr';
import { useIncidentRealtime } from '@ngsos/portal-core/chat';
import { requestAppleEedVideo } from '@ngsos/portal-core/commands';
import { useVideoCall } from './useVideoCall';
import { MediasoupVideoViewer } from './MediasoupVideoViewer';
import { VideoBoxLayout } from './VideoBoxLayout';
import { VideoStatsLink } from './VideoStatsLink';
import {
  canSwapCameraOrTakeSnapshot,
  getDisplayPanelMode,
  getWebAppStateDescriptionKey,
  getWebAppStateIcon,
  getWebAppStateTextKey,
  isRewindDisabled,
} from '@ngsos/portal-core/video';
import type { DisplayPanelMode, VideoCallState } from '@ngsos/portal-core/video';

export type MediasoupVideoBoxProps = {
  incidentId: string;
  liveVideo: LiveVideo;
  videoExpanded: boolean;
  onVideoExpandedChanged: (expanded: boolean) => void;
  allowAppleEedVideoActions: boolean;
  appleEedVideoCallId?: string | null;
};

const FLOATING_STATE_CSS: Record<FloatingPanelState, string> = {
  docked: 'mediasoup-video-box--floating-state-docked',
  floating: 'mediasoup-video-box--floating-state-floating',
  fullscreen: 'mediasoup-video-box--floating-state-fullscreen',
};

export function MediasoupVideoBox({
  incidentId,
  liveVideo,
  videoExpanded,
  onVideoExpandedChanged,
  allowAppleEedVideoActions,
  appleEedVideoCallId,
}: MediasoupVideoBoxProps) {
  void incidentId;
  const { t } = useTranslation();
  const realtime = useIncidentRealtime();

  const resendAppleEedVideoRequest = async () => {
    if (appleEedVideoCallId == null) {
      return;
    }
    await requestAppleEedVideo(appleEedVideoCallId, liveVideo.id);
  };

  const { state, videoServerUrl, toggleRecordingAndPauseOrResumeStream, switchUserCamera, takeSnapshot, endLiveVideo } =
    useVideoCall({ liveVideo, realtime, onVideoExpandedChanged });

  const [floatingPanelState, setFloatingPanelState] = useState<FloatingPanelState>('docked');

  const isAppleEedVideo = liveVideo.source === LiveVideoSource.AppleEed;
  const appleEedCssClass = isAppleEedVideo ? 'mediasoup-video-box--eed' : '';
  const videoExpandedCssClass = videoExpanded ? 'mediasoup-video-box--expanded' : '';
  const floatingStateCssClass = FLOATING_STATE_CSS[floatingPanelState];

  const { showRecording } = state;
  const hideControls = showRecording;
  const liveIndicatorCssClass = showRecording
    ? 'mediasoup-video-box__live--inactive'
    : 'mediasoup-video-box__live--active';
  const liveStreamLabel =
    liveVideo.source === LiveVideoSource.AppleEed
      ? t('Incident_Video_AppleEedLiveStream')
      : liveVideo.source === LiveVideoSource.GoogleEls
        ? t('Incident_Video_GoogleElsLiveStream')
        : t('Incident_Video_WebAppLiveStream');

  const currentDisplayMode = getDisplayPanelMode(state, isAppleEedVideo);

  const onVideoBoxClicked = () => {
    onVideoExpandedChanged(!videoExpanded);
  };

  const webAppStateText = t(getWebAppStateTextKey(state.webAppState));
  const webAppStateDescriptionKey = getWebAppStateDescriptionKey(state.webAppState);
  const webAppStateDescription = webAppStateDescriptionKey !== null ? t(webAppStateDescriptionKey) : '';

  return (
    <FloatingPanel
      state={floatingPanelState}
      onStateChanged={setFloatingPanelState}
      floatingDisabledOnMobile
      disableFloating
      disableFullscreen
      floatingDisabled
    >
      <div
        className={`mediasoup-video-box ${floatingStateCssClass} ${appleEedCssClass} ${videoExpandedCssClass}`}
        onClick={onVideoBoxClicked}
      >
        <VideoStatsLink
          videoId={liveVideo.id}
          videoServerUrl={videoServerUrl}
          className="video-stats-link mediasoup-video-box__stats-link"
        />
        <button
          className="mediasoup-video-box__expand-btn"
          onClick={(event) => {
            event.stopPropagation();
            onVideoBoxClicked();
          }}
          title={t('Incident_Video_ExpandVideo')}
        >
          <FaIcon icon={videoExpanded ? 'fal fa-compress' : 'fal fa-expand'} />
        </button>

        <VideoBoxLayoutContent
          state={state}
          isAppleEedVideo={isAppleEedVideo}
          currentDisplayMode={currentDisplayMode}
          hideControls={hideControls}
          liveIndicatorCssClass={liveIndicatorCssClass}
          liveStreamLabel={liveStreamLabel}
          webAppStateText={webAppStateText}
          webAppStateDescription={webAppStateDescription}
          allowAppleEedVideoActions={allowAppleEedVideoActions}
          appleEedVideoCallId={appleEedVideoCallId ?? null}
          onResendAppleEedVideoRequest={resendAppleEedVideoRequest}
          toggleRecordingAndPauseOrResumeStream={toggleRecordingAndPauseOrResumeStream}
          switchUserCamera={switchUserCamera}
          takeSnapshot={takeSnapshot}
          endLiveVideo={endLiveVideo}
        />
      </div>
    </FloatingPanel>
  );
}

type VideoBoxLayoutContentProps = {
  state: VideoCallState;
  isAppleEedVideo: boolean;
  currentDisplayMode: DisplayPanelMode;
  hideControls: boolean;
  liveIndicatorCssClass: string;
  liveStreamLabel: string;
  webAppStateText: string;
  webAppStateDescription: string;
  allowAppleEedVideoActions: boolean;
  appleEedVideoCallId: string | null;
  onResendAppleEedVideoRequest: () => Promise<void>;
  toggleRecordingAndPauseOrResumeStream: () => Promise<void>;
  switchUserCamera: () => Promise<void>;
  takeSnapshot: () => Promise<void>;
  endLiveVideo: () => Promise<void>;
};

export function VideoBoxLayoutContent({
  state,
  isAppleEedVideo,
  currentDisplayMode,
  hideControls,
  liveIndicatorCssClass,
  liveStreamLabel,
  webAppStateText,
  webAppStateDescription,
  allowAppleEedVideoActions,
  appleEedVideoCallId,
  onResendAppleEedVideoRequest,
  toggleRecordingAndPauseOrResumeStream,
  switchUserCamera,
  takeSnapshot,
  endLiveVideo,
}: VideoBoxLayoutContentProps) {
  const { t } = useTranslation();
  const { showRecording } = state;

  return (
    <VideoBoxLayout
      contentTemplate={
        <>
          {state.callerConnectionState === 'Connected' && (
            <p className={liveIndicatorCssClass}>
              {showRecording ? t('Incident_Video_RecordingPlaying') : liveStreamLabel}
            </p>
          )}

          <div
            className={`mediasoup-video-box__viewer-wrapper ${
              currentDisplayMode === 'LiveVideo' || currentDisplayMode === 'RecordingPlayback' ? '' : 'd-none'
            }`}
          >
            <MediasoupVideoViewer
              showRecording={showRecording}
              connectionState={state.callerConnectionState}
              cameraState={state.cameraState}
            />
          </div>

          {currentDisplayMode === 'FatalError' && (
            <div className="pb-4 text-center">
              <div className="mx-3">
                <PanelErrorWithIcon title={webAppStateText} description={webAppStateDescription} centerContent />
              </div>
            </div>
          )}

          {currentDisplayMode === 'AppleEedInstructions' && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <div>{t('Incident_Video_AppleEedScreenShareRequestSent')}</div>
                {allowAppleEedVideoActions && appleEedVideoCallId != null && (
                  <span onClick={(event) => event.stopPropagation()}>
                    <NgButton
                      text={t('Incident_Button_ResendVideoRequest')}
                      icon="fal fa-video-plus"
                      size="small"
                      showProgress
                      onClick={onResendAppleEedVideoRequest}
                    />
                  </span>
                )}
              </PanelInfoWithIcon>
            </div>
          )}

          {currentDisplayMode === 'Disconnected' && (
            <PanelInfoWithIcon>
              <img src="Images/Incident/video-canceled.svg" alt="Video disconnected" />
              <div>{t('Incident_Video_VideoStreamCancelled')}</div>
              <div>{t('Incident_Video_WaitingForCameraAccessNote')}</div>
            </PanelInfoWithIcon>
          )}

          {currentDisplayMode === 'Connecting' && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <img src={getWebAppStateIcon()} alt="Connecting" />
                <div>{t('Incident_Video_ConnectingToCall')}</div>
              </PanelInfoWithIcon>
            </div>
          )}

          {currentDisplayMode === 'WebAppState' && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <img src={getWebAppStateIcon()} alt="Video status" />
                <div>{webAppStateText}</div>
                <div className="small text-black-50">{webAppStateDescription}</div>
              </PanelInfoWithIcon>
            </div>
          )}

          {currentDisplayMode === 'CameraTurnedOff' && (
            <div className="pb-4 text-center">
              <PanelInfoWithIcon>
                <img src="Images/Incident/video-canceled.svg" alt="Camera disabled" />
                <div>{t('Incident_Video_CameraDisabled')}</div>
                <div>{t('Incident_Video_CameraDisabledByUser')}</div>
              </PanelInfoWithIcon>
            </div>
          )}
        </>
      }
      actionsTemplate={
        <div onClick={(event) => event.stopPropagation()}>
          {!hideControls ? (
            <>
              <div className="btn-group">
                <NgButton
                  onClick={toggleRecordingAndPauseOrResumeStream}
                  type="dark"
                  disabled={isRewindDisabled(state)}
                >
                  -5s
                </NgButton>
                {!isAppleEedVideo && (
                  <>
                    <NgButton
                      icon="fal fa-camera-rotate"
                      title={t('Button_SwapCamera')}
                      disabled={!canSwapCameraOrTakeSnapshot(state)}
                      outline
                      onClick={switchUserCamera}
                    />

                    <NgButton
                      icon="fal fa-camera"
                      title={t('Button_TakeSnapshot')}
                      disabled={!canSwapCameraOrTakeSnapshot(state)}
                      outline
                      onClick={takeSnapshot}
                    />
                  </>
                )}
              </div>

              {!isAppleEedVideo && (
                <span onClick={(event) => event.stopPropagation()}>
                  <NgButton
                    icon="fal fa-video-slash"
                    title={t('Incident_Button_StopVideoStream')}
                    type="danger"
                    onClick={endLiveVideo}
                  />
                </span>
              )}
            </>
          ) : (
            <div className="mediasoup-video-box__go-live">
              <NgButton onClick={toggleRecordingAndPauseOrResumeStream} size="small" type="dark">
                <span>{t('Incident_Button_PlayLiveStream')}</span>
              </NgButton>
            </div>
          )}
        </div>
      }
    />
  );
}
